import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  Country,
  CountryApiItem,
  DeleteCountryResult,
  UpsertCountryRequest,
} from './country.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private readonly endpoint = apiUrl('country');

  constructor(private readonly http: HttpClient) {}

  getCountries(): Observable<Country[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeCountry(item))),
    );
  }

  createCountry(payload: UpsertCountryRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateCountry(id: number, payload: UpsertCountryRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteCountry(id: number): Observable<DeleteCountryResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteCountryResult => ({ kind: 'success' })),
      catchError(() => of<DeleteCountryResult>({
        kind: 'error',
        message: 'Unable to delete country at this time. Please try again.',
      })),
    );
  }

  private normalizeCountry(item: CountryApiItem): Country {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      regionId: this.toNullableNumber(item.regionId),
      regionName: this.toText(item.regionName),
      code: this.toText(item.code),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: CountryApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): CountryApiItem[] {
    if (Array.isArray(response)) {
      return response as CountryApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as CountryApiItem[];
      }

      if (Array.isArray(items)) {
        return items as CountryApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
}
